import { useState, useRef, useEffect } from 'react';
import { decodeJwt } from 'jose';
import { getStoredToken } from '../utils/tokenStorage';
import { initiateLogout } from '../utils/oauth';
import { config } from '../config/env';

interface DecodedToken {
  sub: string;
  email?: string;
  [key: string]: unknown;
}

function getUserInfo(): DecodedToken | null {
  const token = getStoredToken();
  if (!token) return null;

  try {
    return decodeJwt(token) as DecodedToken;
  } catch {
    return null;
  }
}

function UserIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
      />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
      />
    </svg>
  );
}

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [userInfo] = useState<DecodedToken | null>(() => getUserInfo());
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayName = userInfo?.email || userInfo?.sub || 'User';

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 pl-3 pr-2.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-150 border border-white/20 hover:border-white/30 hover:bg-white/10"
        style={{
          backgroundColor: isOpen
            ? 'rgba(255,255,255,0.15)'
            : 'rgba(255,255,255,0.05)',
          color: 'rgba(255,255,255,0.95)',
        }}
      >
        <UserIcon />
        <span className="max-w-[180px] truncate">{displayName}</span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-52 rounded-lg shadow-xl bg-white border border-gray-200 overflow-hidden z-50"
          style={{
            top: '100%',
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(-4px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>

          {/* User info header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Signed in as
            </p>
            <p className="text-sm font-medium text-gray-900 truncate mt-0.5">
              {displayName}
            </p>
          </div>

          {/* Menu items */}
          <div className="py-1.5">
            <a
              href={`${config.apiBaseUrl}/schema.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
              style={{
                color: '#374151',
                textDecoration: 'none',
              }}
              onClick={() => setIsOpen(false)}
            >
              <DatabaseIcon />
              <span>Database Schema</span>
            </a>

            <div className="my-1.5 mx-3 border-t border-gray-100" />

            <button
              onClick={async () => {
                setIsOpen(false);
                await initiateLogout();
              }}
              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors w-full text-left"
              style={{
                color: '#374151',
              }}
            >
              <SignOutIcon />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
